import { useState, useEffect } from "react";
import { useNavigate, Link, useSearchParams, Navigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import Logo from "../components/Logo";
import { Truck, Lock, EnvelopeSimple, User, Buildings, ArrowRight, GoogleLogo, IdentificationCard, Hash, Phone, CheckCircle, Warning, CircleNotch, ShieldCheck, Sparkle, CreditCard } from "@phosphor-icons/react";
import client from "../lib/api";
import { trackConversion, getAttribution, trackStep, getStartVariant, reportExperiment, getExperimentVariant, loadExperimentDefaults, cachedExperimentDefaults } from "../lib/analytics";
import StartProof from "../components/StartProof";

// Plans offered on the direct ad landing (/start). Solo is pre-selected; one tap switches.
const DIRECT_PLANS = [
  { id: "solo", name: "Solo", price: 79, trucks: "1 truck" },
  { id: "duo", name: "Duo", price: 129, trucks: "2 trucks" },
  { id: "trio", name: "Trio", price: 179, trucks: "3 trucks" },
];
const DEFAULT_DIRECT_PLAN = "solo";
// Headline A/B arms for /start
const HEADLINES = {
  steps: { line1: "Two steps and", line2: "you're running.", mobile: "Two steps and you're running.",
    sub: "Create your account, then add a card on Stripe's secure page. $0 today — your first charge is in 7 days unless you cancel." },
  payroll: { line1: "Payroll + IFTA", line2: "done by Friday.", mobile: "Payroll + IFTA done by Friday.",
    sub: "Driver pay by mile, % or salary. IFTA miles by state. Create your account — $0 today, first charge in 7 days unless you cancel." },
};

// Fleet-size options for the signup dropdown. Values marked `enterprise=true`
// route the user to /demo instead of the self-serve trial.
const FLEET_SIZE_OPTIONS = [
  { value: "", label: "Select your fleet size…" },
  { value: "1", label: "1 truck (Owner-Operator)" },
  { value: "2-3", label: "2–3 trucks" },
  { value: "4-10", label: "4–10 trucks" },
  { value: "11-25", label: "11–25 trucks" },
  { value: "26-50", label: "26–50 trucks", enterprise: true },
  { value: "50+", label: "50+ trucks", enterprise: true },
];

export default function AuthPage({ mode = "login", direct = false }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [fullName, setFullName] = useState("");
  const [mcNumber, setMcNumber] = useState("");
  const [dotNumber, setDotNumber] = useState("");
  const [phone, setPhone] = useState("");
  const [fleetSize, setFleetSize] = useState("");
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [verifyResult, setVerifyResult] = useState(null); // {verified, legal_name, city, state, ...} | null
  const { login, signup, user: authedUser, loading: authLoading } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();

  // Direct ad landing (/start): plan pre-selected, straight to the Stripe card page after signup.
  // Pre-selected plan is itself an A/B arm (solo vs duo) unless the link says ?plan= or a winner is locked.
  const [planExp] = useState(() => (direct && !params.get("plan") ? getExperimentVariant("start_plan", ["solo", "duo"]) : null));
  const [plan, setPlan] = useState(() => {
    const p = params.get("plan") || (() => { try { return sessionStorage.getItem("chosen_plan"); } catch { return null; } })();
    if (DIRECT_PLANS.some((x) => x.id === p)) return p;
    return planExp?.variant || DEFAULT_DIRECT_PLAN;
  });
  useEffect(() => {
    if (!direct) return;
    try { sessionStorage.setItem("chosen_plan", plan); sessionStorage.setItem("fp_auto_checkout", "1"); } catch {}
  }, [direct, plan]);
  const directCheckoutUrl = `/app/billing?start=1&plan=${plan}&auto=1`;
  // A/B on /start. Layout (mobile only): "clip" = demo clip above the form, "form" = form first, clip below.
  // Headline (phones + desktop): "steps" vs "payroll". A locked winner (admin "Make default") overrides for everyone.
  const [variant, setVariant] = useState(() => (direct ? getStartVariant() : null));
  const [headline, setHeadline] = useState(() => (direct ? getExperimentVariant("start_headline", ["steps", "payroll"]) : null));
  useEffect(() => {
    if (!direct) return;
    let alive = true;
    loadExperimentDefaults().then((defs) => {
      if (!alive || !defs) return;
      if ((defs.start_layout === "clip" || defs.start_layout === "form") && window.innerWidth < 1024) setVariant(defs.start_layout);
      if (defs.start_headline === "steps" || defs.start_headline === "payroll") setHeadline({ variant: defs.start_headline, locked: true });
      if ((defs.start_plan === "solo" || defs.start_plan === "duo") && !params.get("plan")) setPlan(defs.start_plan);
    });
    return () => { alive = false; };
  }, [direct]); // eslint-disable-line
  useEffect(() => {
    if (!direct) return;
    const defs = cachedExperimentDefaults() || {};
    if (variant && !defs.start_layout) reportExperiment("start_layout", variant);
    if (headline?.variant && !headline.locked && !defs.start_headline) reportExperiment("start_headline", headline.variant);
    if (planExp?.variant && !planExp.locked && !defs.start_plan) reportExperiment("start_plan", planExp.variant);
  }, [direct, variant, headline, planExp]);
  const H = HEADLINES[headline?.variant] || HEADLINES.steps;

  useEffect(() => {
    if (params.get("expired") === "1") {
      toast.info("Your session expired — please sign in again.");
    }
  }, []); // eslint-disable-line

  const isSignup = mode === "signup";
  const selectedFleetOption = FLEET_SIZE_OPTIONS.find((o) => o.value === fleetSize);
  const isEnterpriseFleet = isSignup && Boolean(selectedFleetOption?.enterprise);

  // ---- Referral capture ----
  // Reads ?ref=<code> from URL; if present, validates it, stashes to session,
  // and surfaces a "Invited by X — you get +30 days" banner above the form.
  const [refInfo, setRefInfo] = useState(null);
  useEffect(() => { if (isSignup) trackStep("signup_view"); }, [isSignup]);

  useEffect(() => {
    if (!isSignup) return;
    let code = params.get("ref");
    if (!code) {
      try { code = sessionStorage.getItem("ref_code"); } catch {}
    }
    if (!code) return;
    try { sessionStorage.setItem("ref_code", code); } catch {}
    (async () => {
      try {
        const { data } = await client.get(`/referrals/validate/${encodeURIComponent(code)}`);
        if (data?.valid) setRefInfo({ code, ...data });
      } catch {/* silent — invalid ref just hides banner */}
    })();
  }, [isSignup, params]);

  // Route to /demo when the user picks 26+ trucks — enterprise fleets need a
  // custom onboarding conversation, not a self-serve trial.
  const goToDemo = () => {
    const params = new URLSearchParams();
    if (fullName) params.set("name", fullName);
    if (companyName) params.set("company", companyName);
    if (email) params.set("email", email);
    if (phone) params.set("phone", phone);
    if (fleetSize) params.set("fleet_size", fleetSize);
    const qs = params.toString();
    nav(`/demo${qs ? `?${qs}` : ""}`);
  };

  // FMCSA Auto-Verify — fires on-blur when user finishes typing MC# or DOT#.
  // Displays inline pill: spinner → ✓ Verified: NAME (city, ST) — or ✗ Not found.
  // Soft-warn model — signup still allowed if unverified.
  // Returns the fresh result directly so submit() can await it without React state timing races.
  const verifyCarrier = async () => {
    const dot = dotNumber.trim();
    const mc = mcNumber.trim();
    if (!dot && !mc) {
      setVerifyResult(null);
      return null;
    }
    setVerifying(true);
    try {
      const { data } = await client.post("/fmcsa/verify", {
        dot_number: dot || undefined,
        mc_number: mc || undefined,
      });
      setVerifyResult(data);
      // Auto-fill company name if empty & FMCSA returned an official legal name
      if (data?.verified && !companyName && data.legal_name) {
        setCompanyName(data.legal_name);
      }
      return data;
    } catch (e) {
      const failResult = { verified: false, reason: e?.response?.data?.detail || "Verification unavailable" };
      setVerifyResult(failResult);
      return failResult;
    } finally {
      setVerifying(false);
    }
  };

  // Invalidate stale verify state whenever either identifier changes so a previously
  // verified pill can't govern the on-submit soft-warn after inputs are edited.
  const onMcChange = (v) => {
    setMcNumber(v);
    if (verifyResult) setVerifyResult(null);
  };
  const onDotChange = (v) => {
    setDotNumber(v);
    if (verifyResult) setVerifyResult(null);
  };

  const submit = async (e) => {
    e.preventDefault();
    // Ensure verify runs on-submit if MC/DOT is present but result is stale/missing
    // (guards the Enter-key race where onBlur hasn't fired yet).
    let currentResult = verifyResult;
    if (isSignup && (mcNumber.trim() || dotNumber.trim()) && !currentResult) {
      currentResult = await verifyCarrier();
    }
    // Soft-warn: if MC# or DOT# entered but not FMCSA-verified, ask user to confirm.
    if (isSignup && (mcNumber.trim() || dotNumber.trim()) && currentResult && !currentResult.verified) {
      const ok = window.confirm(
        `We couldn't verify your MC#/DOT# with FMCSA public records (${currentResult.reason || "not found"}). Continue signup anyway? Your account will be flagged for admin review.`
      );
      if (!ok) return;
    }
    setLoading(true);
    try {
      if (isSignup) {
        // Referral code — either in URL query (?ref=code) or previously
        // stashed in sessionStorage (survives redirects like /pricing → /signup).
        let refCode = params.get("ref");
        if (!refCode) {
          try { refCode = sessionStorage.getItem("ref_code") || undefined; } catch {}
        }
        try { sessionStorage.setItem("fp_fresh_signup", "1"); } catch {}
        await signup({
          email, password,
          company_name: companyName, full_name: fullName,
          mc_number: mcNumber.trim() || undefined,
          dot_number: dotNumber.trim() || undefined,
          phone: phone.trim() || undefined,
          ref: refCode || undefined,
          // First-touch attribution — which ad/reel/page brought them here
          ...getAttribution(),
        });
        // Clear stored ref so future signups don't double-attribute
        try { sessionStorage.removeItem("ref_code"); } catch {}
        trackConversion("signup_complete", {
          plan: params.get("plan") || sessionStorage.getItem("chosen_plan") || "unspecified",
          fleet_size: fleetSize || "1",
          referred: !!refCode,
        });
        toast.success("Welcome aboard. Setting up your ops center...");
      } else {
        await login(email, password);
        toast.success("Welcome back.");
      }
      // If user came from pricing with a chosen plan, send them to billing to complete checkout
      let chosenPlan = params.get("plan");
      if (!chosenPlan) {
        try { chosenPlan = sessionStorage.getItem("chosen_plan"); } catch {}
      }
      if (direct && isSignup) {
        // Ad landing: no plan picking — straight to the Stripe card page.
        nav(directCheckoutUrl);
      } else if (chosenPlan) {
        try { sessionStorage.removeItem("chosen_plan"); } catch {}
        nav(`/app/billing?start=1&plan=${chosenPlan}`);
      } else if (isSignup) {
        // Card-required signup: pick a plan and start the 7-day trial before entering the app.
        nav("/app/billing?start=1");
      } else {
        nav("/app/dashboard");
      }
    } catch (err) {
      try { sessionStorage.removeItem("fp_fresh_signup"); } catch {}
      const detail = err?.response?.data?.detail;
      toast.error(Array.isArray(detail) ? (detail[0]?.msg || "Please check the form and try again") : (detail || "Something went wrong"));
    } finally {
      setLoading(false);
    }
  };

  if (direct && !authLoading && authedUser) {
    return <Navigate to={authedUser.needs_checkout ? directCheckoutUrl : "/app/dashboard"} replace />;
  }
  const chosen = DIRECT_PLANS.find((p) => p.id === plan) || DIRECT_PLANS[0];

  return (
    <div className="min-h-screen flex" data-testid={direct ? "start-page" : "auth-page"} data-variant={direct ? (variant || "desktop") : undefined} data-headline={direct ? (headline?.variant || "steps") : undefined} data-plan={direct ? plan : undefined}>
      {/* Left panel — image + branding */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        <img
          src="https://images.pexels.com/photos/11053643/pexels-photo-11053643.jpeg"
          alt="Night truck"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-black/70" />
        <div className={`relative z-10 flex flex-col p-12 w-full ${direct ? "gap-12" : "justify-between"}`}>
          <div className="flex items-center gap-3">
            <Logo />
          </div>
          {direct ? (
            <div className="max-w-xl" data-testid="start-steps">
              <div className="overline mb-4">7-DAY FREE TRIAL · $0 TODAY</div>
              <h1 className="font-display text-4xl xl:text-5xl font-semibold leading-[1.05] mb-6" data-testid="start-headline" data-arm={headline?.variant || "steps"}>
                {H.line1}<br/><span className="text-[#FF5A1F]">{H.line2}</span>
              </h1>
              <div className="grid grid-cols-[260px_1fr] gap-6 items-start">
                <StartProof />
                <ol className="space-y-5">
                  {[
                    ["1", "Create your account", "Name, company, email. 30 seconds."],
                    ["2", "Add a card on Stripe's secure page", "$0 today. First charge in 7 days — cancel in one click before then."],
                    ["3", "You're in", "Driver payroll, IFTA miles by state, carrier packets with open alerts, BrokerCheck, Docs Vault."],
                  ].map(([n, t, d]) => (
                    <li key={n} className="flex gap-4">
                      <span className="h-8 w-8 shrink-0 rounded-full bg-[#FF5A1F] text-black font-mono font-bold flex items-center justify-center">{n}</span>
                      <div><div className="text-white font-semibold">{t}</div><div className="text-neutral-400 text-sm leading-relaxed">{d}</div></div>
                    </li>
                  ))}
                </ol>
              </div>
            </div>
          ) : (
            <div className="max-w-md">
              <div className="overline mb-4">AI OPS COPILOT</div>
              <h1 className="font-display text-5xl font-semibold leading-[1.05] mb-6">
                Dispatching made simple.<br/>
                <span className="text-[#FF5A1F]">Paperwork made instant.</span>
              </h1>
              <p className="text-neutral-400 text-lg leading-relaxed">
                FreightPilot AI™ helps trucking companies work smart and easy — dispatching, paperwork,
                and staying connected with drivers, shippers, and receivers, all in one place.
              </p>
            </div>
          )}
          {!direct && <div className="grid grid-cols-3 gap-6 text-xs">
            <div>
              <div className="overline mb-1">FREE TRIAL</div>
              <div className="font-mono text-2xl font-semibold">7 days</div>
            </div>
            <div>
              <div className="overline mb-1">CHARGED TODAY</div>
              <div className="font-mono text-2xl font-semibold">$0</div>
            </div>
            <div>
              <div className="overline mb-1">CANCEL</div>
              <div className="font-mono text-2xl font-semibold">Anytime</div>
            </div>
          </div>}
        </div>
      </div>

      {/* Right panel — form */}
      <div className="flex-1 flex items-center justify-center p-8 pb-40 sm:pb-8 bg-[#0A0A0A]">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <Logo />
          </div>

          <div className="overline mb-3">{direct ? "7-DAY FREE TRIAL · CARD REQUIRED · CANCEL ANYTIME" : isSignup ? "GET STARTED" : "SIGN IN"}</div>
          <h2 className="font-display text-4xl font-semibold mb-2 tracking-tight" data-testid={direct ? "start-mobile-headline" : undefined}>
            {direct ? <><span className="lg:hidden">{H.mobile}</span><span className="hidden lg:inline">Create your account</span></> : isSignup ? "Start automating today" : "Back to ops"}
          </h2>
          <p className="text-neutral-400 mb-6">
            {direct
              ? <><span className="lg:hidden">{H.sub}</span><span className="hidden lg:inline">Next: add a card on Stripe's secure page. $0 today — your first charge is in 7 days unless you cancel.</span></>
              : isSignup ? "Set up your fleet's AI operations center in seconds." : "Sign in to your operations dashboard."}
          </p>

          {direct && variant !== "form" && <StartProof compact autoplay={variant === "clip"} />}

          {direct && (
            <div className="mb-6" data-testid="start-plan-switcher">
              <div className="flex gap-2">
                {DIRECT_PLANS.map((p) => (
                  <button key={p.id} type="button" onClick={() => setPlan(p.id)} data-testid={`start-plan-${p.id}`} aria-pressed={plan === p.id}
                    className={`flex-1 rounded-xl border px-3 py-2.5 text-left transition-colors ${plan === p.id ? "border-[#FF5A1F] bg-[#FF5A1F]/10" : "border-white/10 hover:border-white/25"}`}>
                    <div className="text-sm font-semibold text-white">{p.name} <span className="text-neutral-500 font-normal">· {p.trucks}</span></div>
                    <div className="text-xs text-neutral-400"><span className="text-white font-mono">${p.price}</span>/mo after trial</div>
                  </button>
                ))}
              </div>
              <div className="mt-2 text-xs text-neutral-500 inline-flex items-center gap-1.5" data-testid="start-plan-summary">
                <CreditCard size={13} /> {chosen.name} plan · $0 today · ${chosen.price}/mo from day 8 · more trucks? Upgrade anytime inside the app.
              </div>
            </div>
          )}

          {refInfo && isSignup && (
            <div
              className="rounded-xl border border-[#FF5A1F]/40 bg-gradient-to-br from-[#FF5A1F]/[0.10] to-transparent p-4 mb-6 flex items-start gap-3"
              data-testid="referral-invite-banner"
            >
              <Sparkle size={18} weight="fill" className="text-[#FF5A1F] shrink-0 mt-0.5" />
              <div>
                <div className="text-sm text-white font-semibold">
                  Invited by {refInfo.referrer_first_name}
                  {refInfo.referrer_company && (
                    <span className="text-neutral-400 font-normal"> · {refInfo.referrer_company}</span>
                  )}
                </div>
                <div className="text-xs text-neutral-300 mt-1 leading-relaxed">
                  Signup complete → you get a{" "}
                  <b className="text-[#FF9764]">{refInfo.referred_reward_days}-day trial extension</b>{" "}
                  on top of your 7-day free trial (card required, cancel anytime before it ends).
                </div>
              </div>
            </div>
          )}

          {/* REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH */}
          <button
            type="button"
            data-testid="google-signin-btn"
            onClick={() => {
              const redirectUrl = window.location.origin + "/auth/callback";
              window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
            }}
            className="w-full bg-white hover:bg-neutral-100 text-neutral-900 font-medium rounded-full py-3 text-sm inline-flex items-center justify-center gap-3 transition-colors mb-4"
          >
            <GoogleLogo size={18} weight="bold" />
            Continue with Google
          </button>

          <div className="flex items-center gap-3 mb-6">
            <div className="flex-1 h-px bg-white/10" />
            <div className="text-xs text-neutral-500 uppercase tracking-widest">or</div>
            <div className="flex-1 h-px bg-white/10" />
          </div>

          <form onSubmit={submit} className="space-y-5">
            {isSignup && (
              <>
                <Field icon={<User size={16} />} label="Full name" testId="signup-name">
                  <input required value={fullName} onChange={(e) => setFullName(e.target.value)}
                    data-testid="signup-name-input"
                    className="w-full bg-transparent outline-none py-3 text-sm placeholder:text-neutral-600" placeholder="Alex Rivera" />
                </Field>
                <Field icon={<Buildings size={16} />} label="Company name" testId="signup-company">
                  <input required value={companyName} onChange={(e) => setCompanyName(e.target.value)}
                    data-testid="signup-company-input"
                    className="w-full bg-transparent outline-none py-3 text-sm placeholder:text-neutral-600" placeholder="Rivera Logistics" />
                </Field>
                <div className="grid grid-cols-2 gap-3">
                  <Field icon={<IdentificationCard size={16} />} label="MC #" testId="signup-mc">
                    <input value={mcNumber} onChange={(e) => onMcChange(e.target.value)}
                      onBlur={verifyCarrier}
                      data-testid="signup-mc-input"
                      className="w-full bg-transparent outline-none py-3 text-sm placeholder:text-neutral-600" placeholder="MC-123456" />
                  </Field>
                  <Field icon={<Hash size={16} />} label="USDOT #" testId="signup-dot">
                    <input value={dotNumber} onChange={(e) => onDotChange(e.target.value)}
                      onBlur={verifyCarrier}
                      data-testid="signup-dot-input"
                      className="w-full bg-transparent outline-none py-3 text-sm placeholder:text-neutral-600" placeholder="3456789" />
                  </Field>
                </div>
                <FMCSAVerifyBadge verifying={verifying} result={verifyResult} />
                <div className="text-[11px] text-neutral-500 -mt-2 leading-relaxed">
                  These will be auto-included on broker emails so they can verify your authority. Optional — you can add them later in Settings.
                </div>
                <Field icon={<Phone size={16} />} label="Phone (optional)" testId="signup-phone">
                  <input value={phone} onChange={(e) => setPhone(e.target.value)}
                    data-testid="signup-phone-input"
                    className="w-full bg-transparent outline-none py-3 text-sm placeholder:text-neutral-600" placeholder="(555) 123-4567" />
                </Field>
                <Field icon={<Truck size={16} />} label="Fleet size" testId="signup-fleet-size">
                  <select
                    required
                    value={fleetSize}
                    onChange={(e) => setFleetSize(e.target.value)}
                    data-testid="signup-fleet-size-select"
                    className="w-full bg-transparent outline-none py-3 text-sm text-neutral-100 [&>option]:bg-[#141414]"
                  >
                    {FLEET_SIZE_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value} disabled={!o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </Field>
                {isEnterpriseFleet && (
                  <div
                    data-testid="enterprise-fleet-callout"
                    className="rounded-md border border-[#FF5A1F]/30 bg-[#FF5A1F]/5 px-4 py-3 text-xs leading-relaxed"
                  >
                    <div className="flex items-start gap-2">
                      <Sparkle size={14} weight="fill" className="text-[#FF5A1F] mt-0.5 shrink-0" />
                      <div className="flex-1">
                        <div className="font-semibold uppercase tracking-wide text-[#FF9764] mb-1">
                          Enterprise fleet detected
                        </div>
                        <div className="text-neutral-300">
                          Fleets 26+ trucks typically need dedicated onboarding, integrations (ELD, TMS, QuickBooks), and a custom SLA — not a self-serve trial. We'll walk you through the right setup in a 30-min working session.
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
            <Field icon={<EnvelopeSimple size={16} />} label="Email" testId="auth-email">
              <input required type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                data-testid="auth-email-input"
                className="w-full bg-transparent outline-none py-3 text-sm placeholder:text-neutral-600" placeholder="you@fleet.com" />
            </Field>
            <Field icon={<Lock size={16} />} label="Password" testId="auth-password">
              <input required type="password" minLength={6} value={password} onChange={(e) => setPassword(e.target.value)}
                data-testid="auth-password-input"
                className="w-full bg-transparent outline-none py-3 text-sm placeholder:text-neutral-600" placeholder="••••••••" />
            </Field>

            {isEnterpriseFleet ? (
              <button
                type="button"
                onClick={goToDemo}
                data-testid="auth-book-demo-btn"
                className="w-full btn-primary rounded-full py-3.5 text-sm inline-flex items-center justify-center gap-2"
              >
                Book a Demo Instead
                <ArrowRight size={16} weight="bold" />
              </button>
            ) : (
              <button
                type="submit"
                disabled={loading}
                data-testid="auth-submit-btn"
                className="w-full btn-primary rounded-full py-3.5 text-sm inline-flex items-center justify-center gap-2 disabled:opacity-60"
              >
                {loading ? "Please wait..." : (direct ? "Create account → add card" : isSignup ? "Create fleet account" : "Sign in")}
                <ArrowRight size={16} weight="bold" />
              </button>
            )}
          </form>
          {isSignup && (
            <p className="text-xs text-neutral-500 mt-3 text-center" data-testid="auth-trial-terms">
              {direct
                ? <>Next step: Stripe's secure card page for the <b className="text-neutral-300">{chosen.name}</b> plan. $0 today · <b className="text-neutral-300">7-day free trial</b> · cancel anytime before day 8.</>
                : <>Next step: pick a plan and add a card to start your <b className="text-neutral-300">7-day free trial</b>. $0 today · cancel anytime before day 8.</>}
            </p>
          )}
          {direct && variant === "form" && <div className="mt-6"><StartProof compact /></div>}

          <p className="text-sm text-neutral-500 mt-8">
            {isSignup ? "Already dispatching with us?" : "New to FreightPilot AI?"}{" "}
            <Link
              data-testid={isSignup ? "goto-login-link" : "goto-signup-link"}
              to={isSignup ? "/login" : "/signup"}
              className="text-[#FF5A1F] hover:text-[#FF7038] font-medium"
            >
              {isSignup ? "Sign in" : "Create an account"}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}

function Field({ icon, label, children, testId }) {
  return (
    <label className="block" data-testid={`${testId}-field`}>
      <div className="overline mb-2">{label}</div>
      <div className="flex items-center gap-3 px-4 border border-white/10 rounded-md bg-[#141414] focus-within:border-[#FF5A1F]/60 transition-colors">
        <span className="text-neutral-500">{icon}</span>
        {children}
      </div>
    </label>
  );
}

// FMCSA Auto-Verify inline pill — appears below MC#/DOT# inputs on signup.
// States: idle (hidden) | verifying (spinner) | verified (green) | unverified (amber).
function FMCSAVerifyBadge({ verifying, result }) {
  if (verifying) {
    return (
      <div
        data-testid="fmcsa-verify-badge"
        className="flex items-center gap-2 rounded-md border border-white/10 bg-[#141414] px-3 py-2 text-xs text-neutral-400"
      >
        <CircleNotch size={14} className="animate-spin text-[#007AFF]" />
        <span>Checking FMCSA public records…</span>
      </div>
    );
  }
  if (!result) return null;
  if (result.verified) {
    const nameLine = result.legal_name || "Carrier";
    const cityLine = [result.city, result.state].filter(Boolean).join(", ");
    const inactive = result.out_of_service || result.status_code === "I";
    return (
      <div
        data-testid="fmcsa-verify-badge-success"
        className={`flex items-start gap-2 rounded-md border px-3 py-2 text-xs ${inactive ? "border-amber-500/30 bg-amber-500/10" : "border-emerald-500/30 bg-emerald-500/10"}`}
      >
        {inactive ? (
          <Warning size={16} weight="fill" className="text-amber-400 mt-0.5 shrink-0" />
        ) : (
          <ShieldCheck size={16} weight="fill" className="text-emerald-400 mt-0.5 shrink-0" />
        )}
        <div className="flex-1 min-w-0">
          <div className={`font-semibold uppercase tracking-wide ${inactive ? "text-amber-300" : "text-emerald-300"}`}>
            {inactive ? "FMCSA — Inactive Status" : "Verified with FMCSA"}
          </div>
          <div className="text-neutral-200 mt-0.5 truncate">
            {nameLine}
            {cityLine && <span className="text-neutral-500"> · {cityLine}</span>}
          </div>
          <div className="text-neutral-500 mt-0.5">
            {result.dot_number && `DOT ${result.dot_number}`}
            {result.docket_number && ` · ${result.docket_number}`}
            {result.entity_type && ` · ${result.entity_type}`}
          </div>
        </div>
      </div>
    );
  }
  // Unverified — soft-warn tone
  return (
    <div
      data-testid="fmcsa-verify-badge-fail"
      className="flex items-start gap-2 rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs"
    >
      <Warning size={16} weight="fill" className="text-amber-400 mt-0.5 shrink-0" />
      <div className="flex-1">
        <div className="font-semibold uppercase tracking-wide text-amber-300">FMCSA Not Verified</div>
        <div className="text-neutral-300 mt-0.5">
          {result.reason || "Not found in FMCSA public records."} You can still continue — we'll flag your account for review.
        </div>
      </div>
    </div>
  );
}
